import React from 'react';
import { Tooltip } from 'antd';
import {
  CheckOutlined, EditOutlined, StopOutlined,
  ExclamationCircleOutlined, LeftOutlined, RightOutlined,
} from '@ant-design/icons';

// ── Design tokens ─────────────────────────────────────────────────────────────
export const C = {
  navy:        '#162349',
  navyLight:   'rgba(22,35,73,0.08)',
  green:       '#92A372',
  white:       '#FFFFFF',
  bg:          '#F1F5F9',
  border:      '#E2E8F0',
  text:        '#1E293B',
  textMuted:   '#64748B',
  accepted:    '#15803D',
  acceptedBg:  '#DCFCE7',
  evidenceMark:'#FEF08A',
  blue:        '#1E40AF',
  blueBg:      '#DBEAFE',
  red:         '#DC2626',
  redBg:       '#FEE2E2',
};

// Panel widths
export const W = { p1: 290, p2: 330 };

// ── Helper: render OCR text with char_span highlights ─────────────────────────
export const renderOcrWithHighlights = (text, spans) => {
  if (!text) return null;
  if (!spans || spans.length === 0) {
    return <span style={{ whiteSpace: 'pre-wrap', color: C.text }}>{text}</span>;
  }
  // Sort spans, clamp to text length, merge overlaps
  const sorted = [...spans]
    .map(s => ({ start: Math.min(s.start, text.length), end: Math.min(s.end, text.length) }))
    .filter(s => s.end > s.start)
    .sort((a, b) => a.start - b.start);

  const parts = [];
  let cursor = 0;
  sorted.forEach(({ start, end }, i) => {
    if (start > cursor) parts.push({ type: 'text', content: text.slice(cursor, start), key: `t${i}` });
    parts.push({ type: 'mark', content: text.slice(start, end), key: `m${i}` });
    cursor = end;
  });
  if (cursor < text.length) parts.push({ type: 'text', content: text.slice(cursor), key: 'tlast' });

  return (
    <span style={{ whiteSpace: 'pre-wrap', color: C.text, lineHeight: 1.7 }}>
      {parts.map(p =>
        p.type === 'mark'
          ? <mark key={p.key} style={{
              background: C.evidenceMark,
              color: '#1E293B',
              borderRadius: 3,
              padding: '0 2px',
              fontWeight: 600,
            }}>{p.content}</mark>
          : <span key={p.key}>{p.content}</span>
      )}
    </span>
  );
};

// ── HCC / support status badges ───────────────────────────────────────────────
export const HccBadge = ({ mapping }) => {
  if (!mapping?.mapped) return null;
  const badges = [];
  if (mapping.hcc_v28) badges.push({ label: `V28·${mapping.hcc_v28}`, bg: C.blueBg, color: C.blue });
  if (mapping.rxhcc_v08) badges.push({ label: `RXR·${mapping.rxhcc_v08}`, bg: '#EDE9FE', color: '#5B21B6' });
  return (
    <span style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
      {badges.map(b => (
        <span key={b.label} style={{
          fontSize: 13, fontWeight: 700, padding: '1px 5px',
          borderRadius: 3, background: b.bg, color: b.color,
          letterSpacing: '0.04em',
        }}>{b.label}</span>
      ))}
    </span>
  );
};

// Sections whose evidence is considered self-sufficient — no corroboration needed
export const GOSPEL_SECTIONS = new Set(['assessment_plan', 'hpi', 'discharge_summary', 'physical_exam']);

// ── Review status colors (dot in P2 = coder's decision on this code) ────────
export const REVIEW_STATUS = {
  pending:      { color: '#94A3B8', label: 'Pending' },
  accepted:     { color: '#15803D', label: 'Accepted' },
  modified:     { color: '#1D4ED8', label: 'Modified' },
  under_review: { color: '#B45309', label: 'In Review' },
  rejected:     { color: '#DC2626', label: 'Rejected' },
  reverted:     { color: '#94A3B8', label: 'Pending' },
  reset:        { color: '#94A3B8', label: 'Pending' },
  recalled:     { color: '#94A3B8', label: 'Pending' },
};

export const ReviewStatusDot = ({ decision }) => {
  const info = REVIEW_STATUS[decision] || REVIEW_STATUS.pending;
  const isPending = !decision || decision === 'pending' || decision === 'reverted' || decision === 'reset' || decision === 'recalled';
  return (
    <Tooltip title={`Review: ${info.label}`}>
      <span style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 18, height: 18,
        borderRadius: '50%',
        background: isPending ? 'transparent' : info.color,
        border: isPending ? `2px solid ${info.color}` : `4px solid ${info.color}`,
        flexShrink: 0,
        transition: 'all 0.15s',
      }}>
        {decision === 'accepted' && <CheckOutlined style={{ fontSize: 8, color: 'white' }} />}
        {decision === 'modified' && <EditOutlined style={{ fontSize: 8, color: 'white' }} />}
        {decision === 'rejected' && <StopOutlined style={{ fontSize: 8, color: 'white' }} />}
        {decision === 'under_review' && <ExclamationCircleOutlined style={{ fontSize: 8, color: 'white' }} />}
      </span>
    </Tooltip>
  );
};

// ── Support status text label (replaces old SupportDot for support display) ──
export const SupportLabel = ({ supportStatus }) => {
  const map = {
    gospel:       { text: 'Auto Supported', color: '#15803D' },
    corroborated: { text: 'Supported',      color: '#1D4ED8' },
    plausible:    { text: 'Supported',      color: '#1D4ED8' },
    unsupported:  { text: 'Unsupported',    color: '#DC2626' },
  };
  const info = map[supportStatus] || { text: '', color: C.textMuted };
  if (!info.text) return null;
  return (
    <span style={{
      fontSize: 11, fontWeight: 600, color: info.color,
      letterSpacing: '0.02em',
    }}>
      {info.text}
    </span>
  );
};

// ── Audit verdict styles ──────────────────────────────────────────────────────
export const VERDICT_STYLES = {
  suspicious: { bg: '#FEF2F2', border: '#FCA5A5', color: '#991B1B', label: 'SUSPICIOUS' },
  clear:      { bg: '#F0FDF4', border: '#86EFAC', color: '#166534', label: 'CLEAR' },
  error:      { bg: '#F9FAFB', border: '#D1D5DB', color: '#6B7280', label: 'ERROR' },
};